import { use, useMemo, useEffect, useCallback } from 'react';
import { ForwardTo } from '@/lib/utils/utils';
import { PassageDataContext } from '@/data/passage/passage-data-context';
import { getParagraphsFromPassage, LoadingSkeletonPassageDetails } from '@/data/passage/passage-utils';
import { BibleParagraph } from '../bible/bible-paragraph';
import { TranslationDataContext } from '@/data/translation/translation-data-context';
import { DEFAULT_VIEW } from '@/lib/utils/bible-utils';
import { Analytics } from '@/lib/analytics';
import { useManuscript } from '@/context/manuscript-context';
import { ManuscriptErrorBoundary } from '../manuscript/manuscript-error-boundary';
import { ConnectionLayer } from '../manuscript/connection-layer';
import { MarkingBottomSheet } from '../manuscript/marking-bottom-sheet';
import { ManuscriptInteractionLayer } from '../manuscript/manuscript-interaction-layer';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { useActivityLogger } from '@/lib/activity-logger';
import { FootnoteDataContext } from '@/data/footnotes/footnote-data-context';
import { useState } from 'react';
import { Popover, PopoverContent } from '@/components/ui/popover';

interface Props {
    passageId: string;
}

export default function PassageDetails({ passageId }: Props) {
    const isDarkMode = useDarkMode();
    const activityLogger = useActivityLogger();
    const { data: passageData, get } = use(PassageDataContext);
    const { data: translationsData, get: getTranslation } = use(TranslationDataContext);
    const footnoteContext = use(FootnoteDataContext);
    const passage = passageData ? get(passageId) : null;
    const translation = translationsData ? getTranslation(DEFAULT_VIEW.version) : null;

    // Footnote popover state
    const [activeFootnote, setActiveFootnote] = useState<{ data: TranslationFootnote; anchorEl: HTMLElement } | null>(null);

    const paragraphs = useMemo(() => {
        if (!passage || !translation?.paragraphs) {
            return [];
        }
        return getParagraphsFromPassage(translation.paragraphs, passage, translation.version, false);
    }, [translation?.paragraphs, translation?.version, passage]);

    const paragraphsWithVerseInfo = useMemo(() => {
        if (!passage) return paragraphs.map(p => ({ ...p, startVerse: 0, startOffset: 0, footnotesByWord: new Map() }));

        const verseWordCounts: Record<string, number> = {}; // keyed by "ch:v"
        let activeChapter = 0;
        let activeVerse = 0;

        return paragraphs.map((p, index) => {
            const words = p.t.split(' ').filter(w => w);

            // Extract initial chapter and verse from paragraph reference (e.g. "Gen:1:1:1:8")
            const parts = p.r.split(':');
            const refChapter = parseInt(parts[1]);
            const refVerse = parts.length >= 5 ? (parseFloat(parts[2]) === 0 ? 1 : Math.ceil(parseFloat(parts[2]))) : 1;

            if (activeChapter === 0 || p.h > 0) {
                activeChapter = refChapter;
                activeVerse = refVerse;
            }

            const startVerse = activeVerse;
            const verseKey = `${activeChapter}:${activeVerse}`;
            const startOffset = verseWordCounts[verseKey] || 0;

            const footnotesByWord = new Map<number, TranslationFootnote>();
            let wordInVerse = startOffset;

            words.forEach((word, idx) => {
                const features = word.includes('*') ? word.substring(word.indexOf('*') + 1) : '';
                const vPos = features.indexOf('v');
                if (vPos !== -1) {
                    const vVal = features.slice(vPos + 1);
                    if (vVal.includes(':')) {
                        const [c, v] = vVal.split(':');
                        activeChapter = parseInt(c);
                        activeVerse = parseInt(v);
                    } else {
                        activeVerse = parseInt(vVal);
                    }
                    wordInVerse = 0;
                }

                if (footnoteContext?.footnoteMap) {
                    const verseRef = `csb:${passage.book}:${activeChapter}:${activeVerse}`;
                    const verseFootnotes = footnoteContext.getFootnotesForVerse(verseRef);
                    const fn = verseFootnotes.find(f => f.w === wordInVerse);
                    if (fn) {
                        footnotesByWord.set(idx, fn);
                    }
                }

                const currentKey = `${activeChapter}:${activeVerse}`;
                verseWordCounts[currentKey] = (verseWordCounts[currentKey] || 0) + 1;
                wordInVerse++;
            });

            return {
                ...p,
                startVerse,
                startOffset,
                footnotesByWord
            };
        });
    }, [paragraphs, footnoteContext, passage]);

    // Track passage view
    useEffect(() => {
        if (passage) {
            Analytics.passageView(passage.book, passage.ref, passageId);
            activityLogger.log('passage_view', `Viewed ${passage.book} ${passage.ref}`, {
                passageId,
                book: passage.book,
                ref: passage.ref
            });
        }
    }, [passage, passageId, activityLogger]);

    if (!passageData || !translationsData || !paragraphs?.length) {
        return <LoadingSkeletonPassageDetails />;
    }
    if (!passage) {
        return <ForwardTo path="/app" />
    }

    return (
        <>
            {/* Fixed full-screen background for passage view */}

            {/* Foreground passage content */}
            <PassageContentWrapper>
                <div
                    data-passage-text-container
                    className={`relative max-w-3xl px-6 pt-4 border-primary/20 bible-text ${isDarkMode ? 'border-primary/40' : ''}`}
                    style={{ paddingBottom: 'calc(8rem + var(--context-panel-height, 0px))' }}
                >
                    <div style={{ viewTransitionName: `passage-card-${passage.id}` }}>
                        <ConnectionLayer />
                        {paragraphsWithVerseInfo.map((paragraph, index) => (
                            <BibleParagraph
                                key={`passage-${paragraph.r}-${index}`}
                                paragraph={paragraph}
                                version={translation.version}
                                startVerse={paragraph.startVerse}
                                startOffset={paragraph.startOffset}
                                paragraphIndex={index}
                                footnotesByWord={paragraph.footnotesByWord}
                                onFootnoteClick={(footnote, element) => setActiveFootnote({ data: footnote, anchorEl: element })}
                            />
                        ))}
                    </div>
                </div>
            </PassageContentWrapper>

            <ManuscriptErrorBoundary>
                <MarkingBottomSheet />
                <ManuscriptInteractionLayer />
            </ManuscriptErrorBoundary>

            {/* Footnote Popover */}
            {activeFootnote && (
                <Popover open={true} onOpenChange={(open) => !open && setActiveFootnote(null)}>
                    <PopoverContent
                        className="max-w-xs p-3 shadow-md border bg-popover text-popover-foreground"
                        style={{
                            position: 'fixed',
                            top: activeFootnote.anchorEl.getBoundingClientRect().bottom + 4,
                            left: Math.max(10, Math.min(window.innerWidth - 300, activeFootnote.anchorEl.getBoundingClientRect().left))
                        }}
                    >
                        <div className="text-sm leading-relaxed">
                            {activeFootnote.data.t && (
                                <p className={activeFootnote.data.c ? "mb-2" : ""}>
                                    {parseFootnoteText(activeFootnote.data.t)}
                                </p>
                            )}
                            {activeFootnote.data.c && (
                                <div className="text-xs text-muted-foreground pt-1 border-t border-border/50">
                                    <strong>Cross-refs:</strong> {activeFootnote.data.c}
                                </div>
                            )}
                        </div>
                    </PopoverContent>
                </Popover>
            )}
        </>
    );
}

// Helper to parse footnote text markers (*i for italic, *r for cross-ref styling)
function parseFootnoteText(text: string): string {
    // Simple implementation - just strip markers for now
    return text.replace(/\*[ir]/g, '');
}

/**
 * PassageContentWrapper applies user-select: none when we are in mark editing mode.
 * This prevents native text selection during handle dragging, but allows handle interaction.
 */
function PassageContentWrapper({ children }: { children: React.ReactNode }) {
    const { editingMarkId, drawModeStyleId } = useManuscript();

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (!editingMarkId) return;
        // Allow clicks on handles and buttons to work normally
        const target = e.target as HTMLElement;
        if (target.closest('.range-handle') || target.tagName === 'BUTTON') return;
        // Block all other mouse interactions to prevent selection
        e.preventDefault();
    }, [editingMarkId]);

    return (
        <div
            onMouseDownCapture={handleMouseDown}
            style={{
                ...(editingMarkId ? { userSelect: 'none', WebkitUserSelect: 'none' } : {}),
                ...(drawModeStyleId ? { cursor: 'copy' } : {}) // 'copy' gives a nice "plus" cursor indicating addition/marking
            }}
            className={drawModeStyleId ? "selection:bg-primary/20 selection:text-primary-foreground" : ""}
        >
            {children}
        </div>
    );
}
